import { useRef, useMemo, useEffect } from 'react';
import { postFile } from '@keepeek/api-client';
import Axios from 'axios';
import Dropzone from 'dropzone';
import { Base64 } from 'js-base64';
import { createCustomUploadRetryAxiosInstance } from '../utils.js';
import { useUpload } from './useUpload.js';
import { useUploadUpdate } from './useUploadUpdate.js';

var CHUNK_SIZE = 10 * 1024 * 1024;
var dropzone;
var useDropzone = function (uploadKey, enable) {
    // Element references to avoid hardcoded element ids
    var dropzoneRef = useRef(null);
    var openUploadDialogButtonRef = useRef(null);
    var axiosInstance = useMemo(function () { return createCustomUploadRetryAxiosInstance(); }, []);
    var _a = useUploadUpdate(uploadKey), filesAdded = _a.filesAdded, fileUpdate = _a.fileUpdate, filesCompleted = _a.filesCompleted, fileDescriptorAdded = _a.fileDescriptorAdded, setOpenUploadDialog = _a.setOpenUploadDialog;
    var kpkMediaImports = useUpload(uploadKey).kpkMediaImports;
    useEffect(function () {
        if (dropzone && kpkMediaImports.length === 0) {
            // Clear dropzone files when kpkMediaImports emptied
            console.debug("Clear dropzone files");
            dropzone.removeAllFiles();
        }
    }, [kpkMediaImports]);
    useEffect(function () {
        if (dropzone) {
            // @ts-ignore
            if (enable && typeof dropzone.disabled !== "undefined" && dropzone.disabled === true) {
                dropzone.enable();
                // @ts-ignore
            }
            else if (typeof dropzone.disabled === "undefined") {
                dropzone.disable();
            }
        }
    }, [enable]);
    useEffect(function () {
        if (dropzone === undefined && dropzoneRef.current && openUploadDialogButtonRef.current) {
            setOpenUploadDialog(function () {
                return function () {
                    var _a;
                    console.debug("Open file upload dialog");
                    (_a = openUploadDialogButtonRef.current) === null || _a === void 0 ? void 0 : _a.click();
                };
            });
            var dropZoneOptions = {
                url: function (files) {
                    if (files && files.length > 0 && files[0].descriptor) {
                        // Only one file because multiple upload is disabled when using chunks and binary body: only one file is send in per HTTP request.
                        return "".concat(Axios.defaults.baseURL, "/api/dam/files/").concat(files[0].descriptor.id);
                    }
                    else {
                        return "";
                    }
                },
                chunking: true,
                forceChunking: true,
                chunkSize: CHUNK_SIZE,
                maxFilesize: undefined,
                retryChunks: true,
                retryChunksLimit: 20,
                binaryBody: true,
                parallelUploads: 1,
                clickable: openUploadDialogButtonRef.current,
                method: "PUT",
                headers: {
                    Accept: "*/*"
                },
                addedfiles: function (files) {
                    // Add all files at the same time to avoid render for each file
                    filesAdded(files);
                },
                transformFile: function (file, done) {
                    // Create file descriptor before upload
                    postFile(axiosInstance, {
                        axiosRequestConfig: {
                            headers: {
                                "x-kpk-base64filename": Base64.encode(file.name),
                                "x-kpk-upload-content-length": file.size,
                                "x-kpk-upload-content-type": file.type
                            }
                        }
                    })
                        .then(function (data) {
                        // Add descriptor to file object
                        file.descriptor = data.data;
                        // Add descriptor to descriptors atom
                        fileDescriptorAdded(file);
                        done(file);
                    })["catch"](function () {
                        // @ts-ignore
                        dropzone._errorProcessing([file], "Error creating descriptor");
                        fileUpdate(file, 0);
                    });
                },
                sending: function (file, xhr) {
                    // @ts-ignore : get Dropzone chunk information
                    var chunk = dropzone._getChunk(file, xhr);
                    var start = chunk.index * CHUNK_SIZE;
                    var end = Math.min(start + CHUNK_SIZE, file.size) - 1;
                    if (typeof Axios.defaults.headers.common.Authorization === "string") {
                        // Get JWT token from Axios instance
                        xhr.setRequestHeader("Authorization", Axios.defaults.headers.common.Authorization);
                    }
                    else if (Axios.defaults.auth &&
                        Axios.defaults.auth.username &&
                        Axios.defaults.auth.password) {
                        // Basic auth (useful for storybook)
                        xhr.setRequestHeader("Authorization", "Basic ".concat(Base64.encode("".concat(Axios.defaults.auth.username, ":").concat(Axios.defaults.auth.password))));
                    }
                    xhr.setRequestHeader("x-kpk-base64filename", Base64.encode(file.name));
                    xhr.setRequestHeader("content-range", "bytes ".concat(start, "-").concat(end, "/").concat(file.size));
                    xhr.onerror = function () {
                        var _a;
                        var delay = chunk.retries * 1000 + 1000;
                        console.debug("File with descriptor id ".concat((_a = file.descriptor) === null || _a === void 0 ? void 0 : _a.id, ": retry chunk #").concat(chunk.index, " in ").concat((delay / 1000).toFixed(2), "s"));
                        setTimeout(
                        // @ts-ignore : overide onerror to add some increasing delay between each try
                        function () { return dropzone._handleUploadError([file], xhr); }, delay);
                    };
                },
                uploadprogress: function (file, progress) {
                    fileUpdate(file, progress);
                },
                queuecomplete: function () {
                    // Update file with success or error status
                    if (dropzone && dropzone.files) {
                        // Trigger kpk media import update at end of process
                        filesCompleted(dropzone.files);
                    }
                }
            };
            dropzone = new Dropzone(dropzoneRef.current, dropZoneOptions);
            console.debug("Upload initialized");
        }
        return function () {
            // Destroy dropzone when removing Upload component from React tree (HMR or normal tree mutation)
            if (dropzone) {
                dropzone.destroy();
                dropzone = undefined;
                console.debug("Upload disabled");
            }
        };
    }, [
        dropzoneRef,
        filesAdded,
        filesCompleted,
        fileDescriptorAdded,
        fileUpdate,
        openUploadDialogButtonRef,
        setOpenUploadDialog,
        axiosInstance,
    ]);
    return {
        dropzoneRef: dropzoneRef,
        openUploadDialogButtonRef: openUploadDialogButtonRef
    };
};

export { useDropzone };
//# sourceMappingURL=useDropzone.js.map
